import {
  RealTimeCommunicationConnectionState,
  RealTimeCommunicationDegradedReason,
  RealTimeCommunicationEnvelope,
  RealTimeCommunicationStatus,
} from '@seech/core-ng';
import {
  ChatSessionRealtimeBootstrapResponse,
  ChatSessionResponse,
  NotificationMessage,
} from '@automatick/shared-ng';
import { firstValueFrom } from 'rxjs';
import {
  ConversationPulseKind,
  ConversationPulseSource,
  RealtimeAudienceScope,
  RealtimeRenderSeverity,
  RealtimeRenderState,
  SourceSurface,
  AutomatickEvent,
  NotificationHubContract,
  RealtimeRenderDisplayKind,
  RealtimeRenderDisplayStageLabel,
  RealtimeRenderEventType,
  RealtimeRenderStage,
} from '@automatick/shared-ts';
import {
  buildWebChatRealtimeRecipients,
  buildWebChatRealtimeReplayRequests,
  decodeExactRealtimeIdentity,
  decodeWebChatRealtimeSubscriptionContract,
  projectWebChatJoinedWorkRequestIds,
  type WebChatRealtimeSubscriptionContract,
} from '@automatick/shared-client-ts';
import { buildRealtimeStartOptions } from '../shared/realtime/realtime-start-options';
import {
  buildIdleRealtimeConnectionHealth,
  buildRealtimeConnectionHealth,
  type RealtimeConnectionHealth,
  RealtimeTransportState,
} from '../shared/realtime/realtime-connection-health';
import { WebChatMessageRuntime } from './web-chat-message.runtime';
import { WebChatStateStore } from './web-chat-state.store';
import { WebChatThoughtTraceRuntime } from './web-chat-thought-trace.runtime';
import { WebChatTranscriptRuntime } from './web-chat-transcript.runtime';
import {
  MAX_RENDER_EVENT_HISTORY,
  MAX_PRESENTATION_RENDER_EVENT_HISTORY,
  TRANSCRIPT_REFRESH_DELAY_MS,
  TRANSCRIPT_VISIBLE_EVENT_STATES,
  WebChatCanonicalRenderEvent,
  WebChatLocalRealtimeStatusRenderEvent,
  WebChatLocalRenderEventStage,
  WebChatLocalRenderEventType,
  WebChatRenderEvent,
  WebChatState,
} from './web-chat.models';
import {
  hasPresentationEnvelopes,
  isPresentationLifecycleEvent,
  isTerminalPresentationState,
  upsertPresentationRenderEvent,
} from './web-chat-presentation-events';
import {
  isObjectRecord,
  readRecordProperty,
} from './web-chat-contract-normalizers';
import { WebChatRenderEventCodec } from './web-chat-render-event.codec';
const LOCAL_REALTIME_STATUS_TRACE_ID = 'local:realtime-status';
const LOCAL_REALTIME_STATUS_SEQUENCE = Number.MAX_SAFE_INTEGER;
const LOCAL_REALTIME_STATUS_STREAM_PREFIX = 'local:realtime-status';
const REALTIME_STARTUP_FAILURE_MESSAGE =
  'Live updates are temporarily unavailable. Recent messages will refresh when available.';
const REALTIME_SUBSCRIPTION_FAILURE_MESSAGE =
  'Live updates are connected, but work request updates could not be subscribed.';
const REALTIME_REPLAY_FAILURE_MESSAGE =
  'Live updates are connected, but recent activity could not be recovered. New updates will continue to arrive.';

export class WebChatRealtimeRuntime {
  private readonly renderEventCodec = new WebChatRenderEventCodec();
  private subscription: WebChatRealtimeSubscriptionContract | null = null;
  private startAttemptGeneration = 0;

  constructor(
    private readonly state: WebChatStateStore,
    private readonly messages: WebChatMessageRuntime,
    private readonly thoughtTraces: WebChatThoughtTraceRuntime,
    private readonly transcript: WebChatTranscriptRuntime,
  ) {}

  connectLive(): void {
    const session = this.state.stateSubject.value.session;
    const sessionId = decodeExactRealtimeIdentity(session?.sessionId);
    const threadId =
      session?.threadId == null
        ? null
        : decodeExactRealtimeIdentity(session.threadId);
    if (!session || !sessionId || (session.threadId != null && !threadId)) {
      return;
    }

    this.resetLiveChannelState();
    this.state.activeLiveSessionId = sessionId;
    this.state.activeLiveThreadId = threadId;
    this.state.latestRenderEventSequence = null;
    this.state.patch({
      latestRenderEvent: null,
      latestSearchRenderEvent: null,
      latestHealthCheckRenderEvent: null,
      recentRenderEvents: [],
      presentationRenderEvents: [],
      thoughtChain: [],
      activeThoughtTraceId: null,
      finalRenderMessage: null,
      isThoughtChainActive: false,
    });
    this.state.patch({
      liveTransport: 'connecting',
      liveStatusMessage: 'Connecting live updates...',
    });
    this.ensureRealtimeConnectionStarted(session);
  }

  ensureRealtimeConnectionStarted(session: ChatSessionResponse): void {
    const sessionId = decodeExactRealtimeIdentity(session.sessionId);
    if (!sessionId) {
      return;
    }

    if (this.state.startRealtimeConnectionPromise) {
      const pendingStart = this.state.startRealtimeConnectionPromise;
      void pendingStart
        .finally(() => {
          const activeSession = this.state.stateSubject.value.session;
          if (
            this.state.startRealtimeConnectionPromise ||
            !activeSession ||
            decodeExactRealtimeIdentity(activeSession.sessionId) !==
              sessionId ||
            this.state.activeLiveSessionId !== sessionId
          ) {
            return;
          }

          this.ensureRealtimeConnectionStarted(activeSession);
        })
        .catch(() => undefined);
      return;
    }

    const trackedStart = this.startRealtimeConnection(session);
    this.state.startRealtimeConnectionPromise = trackedStart;
    void trackedStart
      .finally(() => {
        if (this.state.startRealtimeConnectionPromise === trackedStart) {
          this.state.startRealtimeConnectionPromise = null;
        }
      })
      .catch(() => undefined);
  }

  async startRealtimeConnection(session: ChatSessionResponse): Promise<void> {
    const sessionId = decodeExactRealtimeIdentity(session.sessionId);
    const expectedThreadId =
      session.threadId == null
        ? null
        : decodeExactRealtimeIdentity(session.threadId);
    const ownsContext = (): boolean =>
      this.state.activeLiveSessionId === sessionId &&
      this.state.activeLiveThreadId === expectedThreadId;
    if (
      !sessionId ||
      (session.threadId != null && !expectedThreadId) ||
      !ownsContext()
    ) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const startAttempt = ++this.startAttemptGeneration;
    try {
      const bootstrap = await this.loadSessionRealtimeBootstrap(sessionId);
      if (!ownsContext()) {
        return;
      }

      const subscription = decodeWebChatRealtimeSubscriptionContract(
        bootstrap,
        sessionId,
        expectedThreadId,
      );
      if (!subscription) {
        throw new Error('Chat realtime bootstrap contract is invalid.');
      }

      this.state.realtimeBootstrap = bootstrap;
      this.subscription = subscription;
      this.state.activeLiveThreadId = subscription.threadId;
      for (const workRequestId of projectWebChatJoinedWorkRequestIds(
        subscription.initialRecipients,
      )) {
        this.state.joinedWorkRequestIds.add(workRequestId);
      }

      const initialRecipients = this.buildActiveRealtimeRecipients();
      await this.state.realtime.start(
        buildRealtimeStartOptions({
          bootstrap: subscription.bootstrap,
          currentDeviceContext: this.state.currentDeviceContext.snapshot(),
          accessTokenFactory: () => this.getRealtimeAccessTokenAsync(),
          initialRecipients,
          restartOnClose: true,
        }),
      );
      if (!ownsContext()) {
        await this.neutralizeRealtimeAttemptAsync(startAttempt);
        return;
      }
      await this.state.realtime.subscribe(initialRecipients);
      if (!ownsContext()) {
        await this.neutralizeRealtimeAttemptAsync(startAttempt);
        return;
      }

      this.state.patch(
        this.withLocalRealtimeStatusPulseState({
          liveTransport: 'connected',
          liveStatusMessage:
            this.state.stateSubject.value.latestRenderEvent?.message ??
            'Live updates connected.',
        }),
      );
      void this.syncActiveRealtimeReplayRequestsAsync();
    } catch {
      if (!ownsContext()) {
        await this.neutralizeRealtimeAttemptAsync(startAttempt);
        return;
      }
      const liveStatusMessage = REALTIME_STARTUP_FAILURE_MESSAGE;
      this.state.patch(
        this.withLocalRealtimeStatusPulseState({
          realtimeHealth:
            this.buildRealtimeStartupFailureHealth(liveStatusMessage),
          liveTransport: 'disconnected',
          liveStatusMessage,
        }),
      );
    }
  }

  async getRealtimeAccessTokenAsync(): Promise<string> {
    const currentToken = this.state.authService.getCurrentToken();
    if (
      currentToken?.access_token &&
      this.state.authService.isLoggedInNotExpired()
    ) {
      return currentToken.access_token;
    }

    const refreshToken = currentToken?.refresh_token;
    if (!refreshToken) {
      return currentToken?.access_token ?? '';
    }

    try {
      const refreshedToken = await firstValueFrom(
        this.state.authService.newAccessToken(refreshToken),
      );
      return refreshedToken?.access_token ?? currentToken?.access_token ?? '';
    } catch {
      return currentToken?.access_token ?? '';
    }
  }

  async loadSessionRealtimeBootstrap(
    sessionId: string,
  ): Promise<ChatSessionRealtimeBootstrapResponse> {
    const exactSessionId = decodeExactRealtimeIdentity(sessionId);
    if (!exactSessionId) {
      throw new Error('A canonical session identity is required.');
    }

    return firstValueFrom(
      this.state.chatClient.getSessionRealtimeBootstrap({
        sessionId: exactSessionId,
        body: {
          sourceSurface: SourceSurface.WebControl,
          take: MAX_RENDER_EVENT_HISTORY,
          includePendingEvents: false,
        },
      }),
    );
  }

  applyRealtimeStatus(status: RealTimeCommunicationStatus): void {
    const realtimeHealth = this.buildUserSafeRealtimeConnectionHealth(status);
    if (!decodeExactRealtimeIdentity(this.state.activeLiveSessionId)) {
      if (status.state === RealTimeCommunicationConnectionState.Idle) {
        this.state.patch(
          this.withLocalRealtimeStatusPulseState({
            realtimeHealth,
            liveTransport: 'idle',
            liveStatusMessage: '',
          }),
        );
        return;
      }
      this.state.patch({ realtimeHealth });
      return;
    }

    if (status.state === RealTimeCommunicationConnectionState.Connected) {
      this.state.patch(
        this.withLocalRealtimeStatusPulseState({
          realtimeHealth,
          liveTransport: 'connected',
          liveStatusMessage: realtimeHealth.message,
        }),
      );
      return;
    }

    if (
      status.state === RealTimeCommunicationConnectionState.Connecting ||
      status.state === RealTimeCommunicationConnectionState.Synchronizing ||
      status.state === RealTimeCommunicationConnectionState.Reconnecting
    ) {
      this.state.patch(
        this.withLocalRealtimeStatusPulseState({
          realtimeHealth,
          liveTransport: 'connecting',
          liveStatusMessage: realtimeHealth.message,
        }),
      );
      return;
    }

    if (
      status.state === RealTimeCommunicationConnectionState.Disconnected ||
      status.state === RealTimeCommunicationConnectionState.Failed
    ) {
      this.state.patch(
        this.withLocalRealtimeStatusPulseState({
          realtimeHealth,
          liveTransport: 'disconnected',
          liveStatusMessage: realtimeHealth.message,
        }),
      );
    }
  }

  private buildUserSafeRealtimeConnectionHealth(
    status: RealTimeCommunicationStatus,
  ): RealtimeConnectionHealth {
    const realtimeHealth = buildRealtimeConnectionHealth(
      status,
      this.state.stateSubject.value.latestRenderEvent?.message ??
        'Live updates connected.',
    );
    let message = realtimeHealth.message;
    if (
      status.lastError &&
      status.state === RealTimeCommunicationConnectionState.Failed
    ) {
      message = REALTIME_STARTUP_FAILURE_MESSAGE;
    } else if (
      status.lastError &&
      status.state === RealTimeCommunicationConnectionState.Disconnected
    ) {
      message = 'Live updates disconnected.';
    }

    return {
      ...realtimeHealth,
      message,
      lastError: null,
    };
  }

  private buildRealtimeStartupFailureHealth(
    message: string,
  ): RealtimeConnectionHealth {
    return {
      ...this.state.stateSubject.value.realtimeHealth,
      state: RealTimeCommunicationConnectionState.Failed,
      transportState: RealtimeTransportState.Disconnected,
      isConnected: false,
      message,
      lastError: null,
      degradedReason: RealTimeCommunicationDegradedReason.StartupFailed,
    };
  }

  private surfaceRealtimeDegradation(
    message: string,
    degradedReason: RealTimeCommunicationDegradedReason,
  ): void {
    this.state.patch({
      liveStatusMessage: message,
      realtimeHealth: {
        ...this.state.stateSubject.value.realtimeHealth,
        message,
        lastError: null,
        degradedReason,
      },
    });
  }

  private withLocalRealtimeStatusPulseState(
    partial: Partial<WebChatState>,
  ): Partial<WebChatState> {
    const currentState = this.state.stateSubject.value;
    const nextState = { ...currentState, ...partial };
    const liveTransport = partial.liveTransport ?? nextState.liveTransport;
    const persistedThoughtChain = this.removeLocalRealtimeStatusPulse(
      nextState.thoughtChain,
    );
    const hadLocalPulse =
      persistedThoughtChain.length !== nextState.thoughtChain.length;

    if (liveTransport === 'disconnected' && nextState.isAwaitingResponse) {
      const realtimeHealth = partial.realtimeHealth ?? nextState.realtimeHealth;
      return {
        ...partial,
        activeThoughtText: null,
        thoughtChain: [
          ...persistedThoughtChain,
          this.buildLocalRealtimeStatusPulse(
            nextState,
            realtimeHealth,
            partial.liveStatusMessage,
          ),
        ],
        activeThoughtTraceId:
          nextState.activeThoughtTraceId ?? LOCAL_REALTIME_STATUS_TRACE_ID,
        isThoughtChainActive: true,
      };
    }

    if (!hadLocalPulse) {
      return partial;
    }

    const activeThoughtTraceId =
      nextState.activeThoughtTraceId === LOCAL_REALTIME_STATUS_TRACE_ID
        ? null
        : nextState.activeThoughtTraceId;

    return {
      ...partial,
      thoughtChain: persistedThoughtChain,
      activeThoughtTraceId,
      isThoughtChainActive:
        nextState.isAwaitingResponse ||
        persistedThoughtChain.length > 0 ||
        Boolean(activeThoughtTraceId),
    };
  }

  private buildLocalRealtimeStatusPulse(
    state: WebChatState,
    realtimeHealth: RealtimeConnectionHealth,
    statusMessage: string | undefined,
  ): WebChatLocalRealtimeStatusRenderEvent {
    const sessionId =
      decodeExactRealtimeIdentity(state.session?.sessionId) ??
      decodeExactRealtimeIdentity(this.state.activeLiveSessionId);
    const threadId =
      decodeExactRealtimeIdentity(state.session?.threadId) ??
      decodeExactRealtimeIdentity(this.state.activeLiveThreadId);
    const occurredOn =
      realtimeHealth.disconnectedOn ?? new Date().toISOString();
    const workspaceId =
      this.state.appState.getStateSnapshot().activeWorkspaceId ?? '';

    return {
      streamId: `${LOCAL_REALTIME_STATUS_STREAM_PREFIX}:${sessionId ?? 'active'}`,
      audienceScope: RealtimeAudienceScope.User,
      eventType: WebChatLocalRenderEventType.RealtimeConnectionStatus,
      sequence: LOCAL_REALTIME_STATUS_SEQUENCE,
      checkpoint: String(LOCAL_REALTIME_STATUS_SEQUENCE),
      workspaceId,
      requestedByUserId: null,
      sessionId,
      threadId,
      workRequestId: null,
      correlationId: null,
      sourceSurface: SourceSurface.WebControl,
      stage: WebChatLocalRenderEventStage.RealtimeConnectionStatus,
      state: RealtimeRenderState.Disconnected,
      message:
        realtimeHealth.message || statusMessage || 'Live updates disconnected.',
      displayKind: RealtimeRenderDisplayKind.Thought,
      displayStageLabel: RealtimeRenderDisplayStageLabel.Thinking,
      promoteToFinalMessage: false,
      severity: RealtimeRenderSeverity.Warning,
      occurredOn,
      data: null,
      pulse: {
        pulseKind: ConversationPulseKind.FrontendSynthetic,
        pulseSource: ConversationPulseSource.Frontend,
        messageKey: null,
        locale: null,
        variantKey: null,
        isFallback: true,
        isDynamic: false,
        silenceBudgetSeconds: null,
        elapsedSinceLastMeaningfulPulseMs: null,
        operationKey: 'realtime.connection',
        instructionId: null,
        correlationId: null,
      },
      presentations: [],
      compactionNotice: null,
    };
  }

  private removeLocalRealtimeStatusPulse(
    entries: WebChatRenderEvent[],
  ): WebChatRenderEvent[] {
    return entries.filter(
      (entry) =>
        entry.eventType !==
        WebChatLocalRenderEventType.RealtimeConnectionStatus,
    );
  }

  handleRealtimeNotification(
    notification: NotificationMessage | null | undefined,
  ): void {
    if (
      !notification ||
      notification.eventName !== AutomatickEvent.RealtimeRenderEvent
    ) {
      return;
    }

    this.handleRealtimeRenderPayload(notification.realtimeRenderEvent);
  }

  handleRealtimeEnvelope(
    envelope: RealTimeCommunicationEnvelope<unknown, unknown>,
  ): void {
    if (this.isNotificationMessage(envelope.payload)) {
      this.handleRealtimeNotification(envelope.payload);
      return;
    }

    if (envelope.eventName === AutomatickEvent.RealtimeRenderEvent) {
      this.handleRealtimeRenderPayload(envelope.payload);
    }
  }

  handleRealtimeRenderPayload(payload: unknown): void {
    const renderEvent = this.normalizeRenderEvent(payload);
    if (!renderEvent || !this.shouldAcceptRenderEvent(renderEvent)) {
      return;
    }

    this.ingestRenderEvent(renderEvent);
  }

  isNotificationMessage(value: unknown): value is NotificationMessage {
    return (
      isObjectRecord(value) &&
      typeof readRecordProperty(value, 'eventName') === 'string'
    );
  }

  normalizeRenderEvent(value: unknown): WebChatCanonicalRenderEvent | null {
    return this.renderEventCodec.decode(value);
  }

  shouldAcceptRenderEvent(renderEvent: WebChatCanonicalRenderEvent): boolean {
    const activeWorkspaceId =
      this.state.appState.getStateSnapshot().activeWorkspaceId ?? null;
    if (!activeWorkspaceId || renderEvent.workspaceId !== activeWorkspaceId) {
      return false;
    }

    const activeSessionId = decodeExactRealtimeIdentity(
      this.state.stateSubject.value.session?.sessionId,
    );
    const activeThreadId = decodeExactRealtimeIdentity(
      this.state.activeLiveThreadId,
    );
    const eventSessionId = renderEvent.sessionId;
    const eventThreadId = renderEvent.threadId;
    const eventWorkRequestId = renderEvent.workRequestId;

    if (eventSessionId) {
      return Boolean(activeSessionId && eventSessionId === activeSessionId);
    }

    if (eventThreadId) {
      return Boolean(activeThreadId && eventThreadId === activeThreadId);
    }

    if (eventWorkRequestId) {
      return this.state.joinedWorkRequestIds.has(eventWorkRequestId);
    }

    return false;
  }

  ingestRenderEvent(renderEvent: WebChatCanonicalRenderEvent): void {
    const currentState = {
      ...this.state.stateSubject.value,
      thoughtChain: this.removeLocalRealtimeStatusPulse(
        this.state.stateSubject.value.thoughtChain,
      ),
    };
    if (
      currentState.recentRenderEvents.some(
        (candidate) => candidate.sequence === renderEvent.sequence,
      )
    ) {
      return;
    }

    this.state.latestRenderEventSequence = Math.max(
      this.state.latestRenderEventSequence ?? 0,
      renderEvent.sequence,
    );

    const isSearchRenderEvent = this.isSearchRenderEvent(renderEvent);
    const isHealthCheckRenderEvent = this.isHealthCheckRenderEvent(renderEvent);
    const isPresentationLifecycle = isPresentationLifecycleEvent(
      renderEvent,
      currentState.presentationRenderEvents,
    );
    const isTerminalPresentationLifecycle =
      isPresentationLifecycle && isTerminalPresentationState(renderEvent);
    const presentationRenderEvents = isPresentationLifecycle
      ? upsertPresentationRenderEvent(
          currentState.presentationRenderEvents,
          renderEvent,
          MAX_PRESENTATION_RENDER_EVENT_HISTORY,
        )
      : currentState.presentationRenderEvents;
    const recentRenderEvents = [
      renderEvent,
      ...currentState.recentRenderEvents.filter(
        (candidate) =>
          (!isSearchRenderEvent || !this.isSearchRenderEvent(candidate)) &&
          (!isHealthCheckRenderEvent ||
            !this.isHealthCheckRenderEvent(candidate)),
      ),
    ]
      .sort((left, right) => right.sequence - left.sequence)
      .slice(0, MAX_RENDER_EVENT_HISTORY);

    const thoughtPatch: Partial<WebChatState> = {};
    if (
      this.isThoughtLikeRenderEvent(renderEvent) &&
      renderEvent.message.length > 0
    ) {
      thoughtPatch.activeThoughtText = renderEvent.message;
    }

    const traceUpdate = this.thoughtTraces.upsertThoughtTraceFromRenderEvent(
      currentState,
      renderEvent,
    );
    thoughtPatch.thoughtTraces = traceUpdate.thoughtTraces;
    thoughtPatch.thoughtTraceEntriesById = traceUpdate.thoughtTraceEntriesById;
    thoughtPatch.activeThoughtTraceId = traceUpdate.activeThoughtTraceId;
    thoughtPatch.thoughtChain = traceUpdate.activeThoughtEntries;
    thoughtPatch.isThoughtChainActive = traceUpdate.isThoughtChainActive;

    const isResponseCompleted =
      renderEvent.eventType ===
        RealtimeRenderEventType.ConversationResponseCompleted ||
      (renderEvent.stage === RealtimeRenderStage.Response &&
        renderEvent.state === RealtimeRenderState.Completed);
    if (isResponseCompleted || isTerminalPresentationLifecycle) {
      thoughtPatch.activeThoughtText = null;
      thoughtPatch.isAwaitingResponse = false;
      thoughtPatch.activeThoughtTraceId = null;
      thoughtPatch.thoughtChain = [];
      thoughtPatch.isThoughtChainActive = false;
      this.state.clearAwaitingResponseTimeout();
    }

    this.state.patch({
      liveTransport: 'connected',
      liveStatusMessage: renderEvent.message,
      latestRenderEvent: renderEvent,
      latestSearchRenderEvent: isSearchRenderEvent
        ? renderEvent
        : currentState.latestSearchRenderEvent,
      latestHealthCheckRenderEvent: isHealthCheckRenderEvent
        ? renderEvent
        : currentState.latestHealthCheckRenderEvent,
      recentRenderEvents,
      presentationRenderEvents,
      ...thoughtPatch,
    });

    if (renderEvent.workRequestId) {
      void this.joinWorkRequestRealtimeAsync(renderEvent.workRequestId);
    }

    if (this.shouldRefreshTranscriptForRenderEvent(renderEvent)) {
      this.scheduleTranscriptRefresh(TRANSCRIPT_REFRESH_DELAY_MS, 0, true);
    }
  }

  shouldRefreshTranscriptForRenderEvent(
    renderEvent: WebChatRenderEvent,
  ): boolean {
    if (hasPresentationEnvelopes(renderEvent)) {
      return false;
    }

    return (
      renderEvent.eventType ===
        RealtimeRenderEventType.ConversationRequirementWait ||
      renderEvent.eventType ===
        RealtimeRenderEventType.ConversationResponseCompleted ||
      renderEvent.stage === RealtimeRenderStage.Response ||
      renderEvent.stage === RealtimeRenderStage.Clarification ||
      renderEvent.stage === RealtimeRenderStage.Requirements ||
      (TRANSCRIPT_VISIBLE_EVENT_STATES.has(renderEvent.state) &&
        renderEvent.state !== RealtimeRenderState.Updated &&
        renderEvent.state !== RealtimeRenderState.Started)
    );
  }

  async joinWorkRequestRealtimeAsync(workRequestId: string): Promise<void> {
    const exactWorkRequestId = decodeExactRealtimeIdentity(workRequestId);
    if (
      !exactWorkRequestId ||
      this.state.joinedWorkRequestIds.has(exactWorkRequestId)
    ) {
      return;
    }

    const activeSessionId = decodeExactRealtimeIdentity(
      this.state.activeLiveSessionId,
    );
    if (!activeSessionId) {
      return;
    }

    this.state.joinedWorkRequestIds.add(exactWorkRequestId);
    try {
      await this.state.realtime.subscribe(this.buildActiveRealtimeRecipients());
    } catch {
      this.state.joinedWorkRequestIds.delete(exactWorkRequestId);
      if (this.state.activeLiveSessionId === activeSessionId) {
        this.surfaceRealtimeDegradation(
          REALTIME_SUBSCRIPTION_FAILURE_MESSAGE,
          RealTimeCommunicationDegradedReason.SubscribeFailed,
        );
      }
      return;
    }

    void this.syncActiveRealtimeReplayRequestsAsync();
  }

  async syncActiveRealtimeReplayRequestsAsync(): Promise<void> {
    const activeSessionId = decodeExactRealtimeIdentity(
      this.state.activeLiveSessionId,
    );
    if (!activeSessionId) {
      return;
    }

    try {
      await this.state.realtime.setReplayRequests(
        this.buildActiveRealtimeReplayRequests(),
      );
    } catch {
      if (this.state.activeLiveSessionId === activeSessionId) {
        this.surfaceRealtimeDegradation(
          REALTIME_REPLAY_FAILURE_MESSAGE,
          RealTimeCommunicationDegradedReason.ReplayFailed,
        );
      }
    }
  }

  private buildActiveRealtimeRecipients(): ReturnType<
    typeof buildWebChatRealtimeRecipients
  > {
    return buildWebChatRealtimeRecipients({
      subscription: this.subscription,
      sessionId: this.state.activeLiveSessionId,
      threadId: this.state.activeLiveThreadId,
      joinedWorkRequestIds: [...this.state.joinedWorkRequestIds],
    });
  }

  private buildActiveRealtimeReplayRequests(): ReturnType<
    typeof buildWebChatRealtimeReplayRequests
  > {
    return buildWebChatRealtimeReplayRequests({
      subscription: this.subscription,
      sessionId: this.state.activeLiveSessionId,
      joinedWorkRequestIds: [...this.state.joinedWorkRequestIds],
      latestRenderEventSequence: this.state.latestRenderEventSequence,
      take: MAX_RENDER_EVENT_HISTORY,
    });
  }

  private async neutralizeRealtimeAttemptAsync(
    startAttempt: number,
  ): Promise<void> {
    if (this.startAttemptGeneration !== startAttempt) {
      return;
    }
    try {
      await Promise.all([
        this.state.realtime.subscribe([]),
        this.state.realtime.setReplayRequests([]),
      ]);
    } catch {
      if (this.startAttemptGeneration === startAttempt) {
        await this.state.realtime.disconnect().catch(() => undefined);
      }
    }
  }

  scheduleTranscriptRefresh(
    delayMilliseconds = TRANSCRIPT_REFRESH_DELAY_MS,
    retryBudget = 0,
    deltaOnly = false,
  ): void {
    this.transcript.scheduleTranscriptRefresh(
      delayMilliseconds,
      retryBudget,
      deltaOnly,
    );
  }

  refreshTranscriptForActiveSession(): void {
    this.transcript.refreshTranscriptForActiveSession();
  }

  resetLiveChannelState(): void {
    void this.state.realtime.subscribe([]).catch(() => undefined);
    void this.state.realtime.setReplayRequests([]).catch(() => undefined);
    this.state.realtimeBootstrap = null;
    this.subscription = null;
    this.state.activeLiveSessionId = null;
    this.state.activeLiveThreadId = null;
    this.state.latestRenderEventSequence = null;
    this.state.lastOutboundTimestamp = null;
    this.state.cancelActiveRequestTransport();
    this.state.joinedWorkRequestIds.clear();
    this.transcript.reset();
    this.state.clearAwaitingResponseTimeout();

    this.state.patch({
      liveTransport: 'idle',
      liveStatusMessage: '',
      realtimeHealth: buildIdleRealtimeConnectionHealth(
        NotificationHubContract.ReceiveMethod,
      ),
      isAwaitingResponse: false,
      isStoppingResponse: false,
      activeThoughtText: null,
      thoughtChain: [],
      activeThoughtTraceId: null,
      finalRenderMessage: null,
      isThoughtChainActive: false,
    });
  }

  disconnectLive(): void {
    this.resetLiveChannelState();
    void this.state.realtime.disconnect();
  }

  private isThoughtLikeRenderEvent(
    renderEvent: WebChatRenderEvent | null | undefined,
  ): boolean {
    return this.messages.isThoughtLikeRenderEvent(renderEvent);
  }

  private isSearchRenderEvent(
    renderEvent: WebChatRenderEvent | null | undefined,
  ): boolean {
    return this.messages.isSearchRenderEvent(renderEvent);
  }

  private isHealthCheckRenderEvent(
    renderEvent: WebChatRenderEvent | null | undefined,
  ): boolean {
    return this.messages.isHealthCheckRenderEvent(renderEvent);
  }
}
